<template>
  <s-modal
    v-model="showModal"
    class="password-modal"
    centered
    :title="$t('my-data.password-modal.change-password')"
    :ok-title="$t('general.conclude')"
    :ok-disabled="!canSave"
    :is-loading="loading"
    @show="show"
    @ok="save"
    @cancel="closeModal"
  >
    <div class="password-modal__body">
      <div
        role="group"
        class="password-modal__field-group"
      >
        <label
          class="password-modal__field-label"
          for="oldPassword"
        >
          {{ $t('my-data.password-modal.current-password') }}
        </label>
        <password-eye
          v-model="oldPassword"
          id="oldPassword"
          maxlength="20"
          type="password"
          :state="$bsStateValidator($v.oldPassword.$error)"
          :disabled="loading"
          @blur.native="$v.oldPassword.$touch()"
        />
      </div>
      <div
        role="group"
        class="password-modal__field-group"
      >
        <label
          class="password-modal__field-label"
          for="newPassword"
        >
          {{ $t('my-data.password-modal.new-password') }}
        </label>
        <i
          class="fas fa-info-circle password-modal__new-password-info"
          aria-hidden="true"
          id="password-modal__new-password-info"
        />
        <password-eye
          v-model="newPassword"
          id="newPassword"
          type="password"
          maxlength="20"
          :state="$bsStateValidator($v.newPassword.$error)"
          :disabled="loading"
          @blur.native="$v.newPassword.$touch()"
        />
        <password-strength-bar :value="strength" />
      </div>
      <div
        class="password-modal__field-group"
        role="group"
      >
        <label
          class="password-modal__field-label"
          for="confirmNewPassword"
        >
          {{ $t('my-data.password-modal.confirm-new-password') }}
        </label>
        <password-eye
          v-model="confirmNewPassword"
          id="confirmNewPassword"
          type="password"
          maxlength="20"
          :state="$bsStateValidator($v.confirmNewPassword.$error)"
          :disabled="loading"
          @blur.native="$v.confirmNewPassword.$touch()"
        />
      </div>
      <div
        class="password-modal__captcha"
        role="group"
      >
        <captcha
          v-if="showCaptcha"
          ref="captcha"
        />
      </div>
    </div>
    <b-tooltip
      target="confirmNewPassword"
      placement="right"
      triggers=""
      :show="$v.confirmNewPassword.$error && !$v.confirmNewPassword.confirmValue"
    >
      <span v-html="$t('my-data.password-modal.wrong-confirmation')"></span>
    </b-tooltip>
    <b-tooltip
      target="password-modal__new-password-info"
      placement="right"
    >
      <span v-html="$t('my-data.password-modal.new-password-tips')"></span>
    </b-tooltip>
  </s-modal>
</template>
<script>
import { required } from 'vuelidate/lib/validators';
import PasswordEye from '@/components/inputs/PasswordEye';
import Captcha from '@/components/captcha/Captcha';
import PasswordStrengthCalculator, { STRENGTH } from '@/services/passwordStrengthCalculator';
import PasswordStrengthBar from '@/components/passwordStrength/StrengthBar';
import commonCaptchaService from '@/services/security/common';
import UserService from '@/domain/services/user';

export default {
  props: {
    value: {
      type: Boolean,
    },
  },
  components: {
    PasswordStrengthBar,
    Captcha,
    PasswordEye,
  },
  data() {
    return {
      oldPassword: null,
      newPassword: null,
      confirmNewPassword: null,
      showCaptcha: false,
      loading: false,
    };
  },
  validations: {
    oldPassword: {
      required,
    },
    newPassword: {
      required,
    },
    confirmNewPassword: {
      required,
      confirmValue(value) { return value === this.newPassword; },
    },
  },
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    strength() {
      return PasswordStrengthCalculator.calculateStrength(this.newPassword);
    },
    canSave() {
      return this.$v.oldPassword.required
        && this.$v.newPassword.required
        && this.strength >= STRENGTH.weak
        && this.$v.confirmNewPassword.required;
    },
  },
  methods: {
    clear() {
      this.oldPassword = null;
      this.newPassword = null;
      this.confirmNewPassword = null;
      this.showCaptcha = false;
      this.$v.$reset();
    },
    show() {
      this.clear();
    },
    save() {
      if (!this.$v.confirmNewPassword.confirmValue) {
        this.$pushAlert({
          code: 'MA002',
        });
        return;
      }

      if (this.showCaptcha) {
        const { captcha } = this.$refs;
        captcha.validateCaptcha()
          .then(() => {
            this.doServiceSave();
          })
          .catch(() => {
            captcha.getCaptcha();
          });

        return;
      }

      this.doServiceSave();
    },
    doServiceSave() {
      this.loading = true;

      return UserService.changePassword(this.oldPassword, this.newPassword)
        .then(() => {
          this.showModal = false;
          this.$pushAlert({
            code: 'MS002',
          });
        })
        .catch((error) => {
          const errorData = commonCaptchaService.extractErrorInformation(error);

          if (commonCaptchaService.shouldShowCaptcha(errorData.blockedUser,
            errorData.wrongAttempts)) {
            if (this.showCaptcha) {
              this.$refs.captcha.getCaptcha();
            }
            this.showCaptcha = true;
            return;
          }
          this.showCaptcha = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    closeModal() {
      this.showModal = false;
    },
  },
};
</script>
<style lang="sass" scoped>
  @import "src/styles/defaults/variables"
  @import "src/styles/defaults/mobile-first"

  .password-modal
    &__new-password-info
      color: $color-link
    &__field-group
      margin-bottom: 20px
      &:first-child
        margin-top: 20px
    &__field-label
      font-weight: 600
    &__body
      display: flex
      flex-direction: column
      align-items: center
    &__captcha
      display: flex
      flex-direction: column
      align-items: center
      width: 100%
      margin-bottom: 20px
    +media-min-sm()
    +media-min-md()
    +media-min-lg()
</style>
